import React from 'react';
import { ResourceType } from '@/hooks/useResourceOptions';

interface ResourceTypeListProps {
    types: ResourceType[];
    selectedTypeId?: string;
    onSelect: (type: ResourceType) => void;
    loading: boolean;
    error: string | null;
    disabled?: boolean;
    emptyMessage?: string;
}

const emptyStateMessage = 'Nenhum tipo cadastrado.';

const ResourceTypeList: React.FC<ResourceTypeListProps> = ({
    types,
    selectedTypeId,
    onSelect,
    loading,
    error,
    disabled = false,
    emptyMessage
}) => {
    if (loading) {
        return (
            <div className="border border-gray-200 rounded-md p-4 text-sm text-gray-500 bg-gray-50">
                Carregando tipos de capacidade...
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-red-200 rounded-md p-4 text-sm text-red-600 bg-red-50">
                {error}
            </div>
        );
    }

    if (!types.length) {
        return (
            <div className="border border-gray-200 rounded-md p-4 text-sm text-gray-500 bg-gray-50">
                {emptyMessage ?? emptyStateMessage}
            </div>
        );
    }

    return (
        <div className="border border-gray-200 rounded-md bg-white">
            <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
                <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
                    Tipos de capacidade
                </p>
                <p className="text-xs text-gray-500 mt-1">
                    Escolha um tipo para visualizar as tecnologias relacionadas.
                </p>
            </div>
            <div
                role="listbox"
                aria-label="Tipos de capacidade"
                className="max-h-60 overflow-y-auto"
            >
                {types.map((type) => {
                    const isSelected = type.id === selectedTypeId;
                    return (
                        <button
                            key={type.id}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            disabled={disabled}
                            onClick={() => onSelect(type)}
                            className={`w-full text-left px-3 py-2 transition-colors border-b border-gray-100 last:border-b-0 ${
                                isSelected
                                    ? 'bg-indigo-50 text-indigo-700'
                                    : 'hover:bg-gray-50 text-gray-700'
                            } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                            title={type.description || type.name}
                        >
                            <span className="block text-sm font-medium">
                                {type.name}
                            </span>
                            {type.description ? (
                                <span className="block text-xs text-gray-500 mt-1">
                                    {type.description}
                                </span>
                            ) : (
                                <span className="block text-xs text-gray-400 mt-1">
                                    Sem descrição cadastrada.
                                </span>
                            )}
                            {type.technologies && (
                                <span className="block text-xs text-gray-400 mt-1">
                                    {type.technologies.length > 0
                                        ? `${type.technologies.length} tecnolog${
                                            type.technologies.length === 1 ? 'ia' : 'ias'
                                        } disponíveis`
                                        : 'Nenhuma tecnologia vinculada'}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ResourceTypeList;
